import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Collapse,
  Badge,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_FUNNEL_DATA_HEALTH_SUMMARY } from "../../graphql/queries.js";
import {
  Dashboard,
  Subscriptions,
  Assignment,
  Route,
  Inventory,
  LocalOffer,
  HealthAndSafety,
  ShoppingCart,
  BarChart,
  Security,
  Settings,
  Tune,
  Menu,
  ChevronLeft,
  HelpOutline,
  ExpandLess,
  ExpandMore,
  Business,
  Storefront,
  Analytics,
  SettingsSuggest,
  Quiz,
  TextFields,
  Category as CategoryIcon,
  Handyman,
  Build,
  Email,
  History,
  ShoppingBag,
  AccountCircle,
  Tune as TuneIcon,
  Storefront as StorefrontIcon,
  Receipt,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher.js";

const DRAWER_WIDTH = 240;
const STORAGE_KEY = "sidebar-groups-open";

/**
 * Gruppierte Navigation (Phase 10.5: zwei Ebenen). Jede Top-Level-Gruppe
 * klappt collapsible auf/zu. Innerhalb der Storefront-Gruppe gibt es
 * zwei Sub-Gruppen (Abo-Funnel, Ersatzteil-Finder), die ebenfalls
 * collapsible sind und ihren eigenen Open-State im selben localStorage-
 * Eintrag persistieren.
 *
 * Die Gruppe mit der aktiven Route öffnet sich automatisch.
 */
interface NavItem {
  path: string;
  icon: React.ReactNode;
  key: string;
}
interface NavSubGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}
type NavEntry = NavItem | NavSubGroup;
function isSubGroup(e: NavEntry): e is NavSubGroup {
  return (e as NavSubGroup).items !== undefined;
}
interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  entries: NavEntry[];
}

// Gruppen-Labels werden nicht statisch hier vergeben, sondern pro
// Render über `t("nav.group.<id>")` aufgelöst — damit der Sprach-
// Umschalter die Sidebar ohne Page-Reload neu rendern kann.
const NAV_GROUPS: NavGroup[] = [
  {
    id: "overview",
    label: "nav.group.overview",
    icon: <Dashboard />,
    entries: [
      { path: "/dashboard", icon: <Dashboard />, key: "dashboard" },
    ],
  },
  {
    id: "storefront",
    label: "nav.group.storefront",
    icon: <StorefrontIcon />,
    entries: [
      { path: "/storefront/pdp-widget", icon: <ShoppingBag />, key: "pdpWidget" },
      {
        id: "subFunnel",
        label: "nav.group.subFunnel",
        icon: <Route />,
        items: [
          { path: "/funnel", icon: <Route />, key: "funnel" },
          { path: "/funnel/quiz", icon: <Quiz />, key: "funnelQuiz" },
          { path: "/funnel/categories", icon: <CategoryIcon />, key: "funnelCategories" },
          { path: "/funnel/product-lines", icon: <Inventory />, key: "products" },
          { path: "/funnel/texts", icon: <TextFields />, key: "funnelTexts" },
          { path: "/funnel/health", icon: <HealthAndSafety />, key: "funnelHealth" },
        ],
      },
      {
        id: "subSpareparts",
        label: "nav.group.subSpareparts",
        icon: <Handyman />,
        items: [
          { path: "/spareparts", icon: <Handyman />, key: "spareparts" },
          { path: "/spareparts/part-types", icon: <Build />, key: "sparepartsPartTypes" },
        ],
      },
      { path: "/storefront/customer-portal", icon: <AccountCircle />, key: "customerPortal" },
    ],
  },
  {
    id: "catalog",
    label: "nav.group.catalog",
    icon: <LocalOffer />,
    entries: [
      { path: "/catalog/tags", icon: <LocalOffer />, key: "catalogTags" },
    ],
  },
  {
    id: "business",
    label: "nav.group.business",
    icon: <Business />,
    entries: [
      { path: "/subscriptions", icon: <Subscriptions />, key: "subscriptions" },
      { path: "/subscriptions/renewals", icon: <Subscriptions />, key: "renewals" },
      { path: "/plans", icon: <Assignment />, key: "plans" },
      { path: "/orders", icon: <ShoppingCart />, key: "orders" },
    ],
  },
  {
    id: "reporting",
    label: "nav.group.reporting",
    icon: <Analytics />,
    entries: [
      { path: "/reporting", icon: <BarChart />, key: "reporting" },
      { path: "/gdpr", icon: <Security />, key: "gdpr" },
      { path: "/audit", icon: <History />, key: "audit" },
    ],
  },
  {
    id: "system",
    label: "nav.group.system",
    icon: <SettingsSuggest />,
    entries: [
      { path: "/settings", icon: <Settings />, key: "systemStatus" },
      { path: "/settings/plan-defaults", icon: <TuneIcon />, key: "planDefaults" },
      { path: "/settings/notifications", icon: <Email />, key: "notifications" },
      { path: "/help", icon: <HelpOutline />, key: "help" },
    ],
  },
];

/** Sammelt alle NavItems einer Gruppe (inkl. Sub-Gruppen) flach. */
function collectAllItems(group: NavGroup): NavItem[] {
  const out: NavItem[] = [];
  for (const e of group.entries) {
    if (isSubGroup(e)) {
      out.push(...e.items);
    } else {
      out.push(e);
    }
  }
  return out;
}

function renderNavItem(
  item: NavItem,
  activePath: string | undefined,
  navigate: (path: string) => void,
  isMobile: boolean,
  setDrawerOpen: (v: boolean) => void,
  t: (k: string) => string,
  funnelIssueCount: number,
  depth: number,
) {
  const isSelected = item.path === activePath;
  return (
    <ListItem key={item.path} disablePadding sx={{ position: "relative" }}>
      {isSelected && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: "0 3px 3px 0",
            backgroundColor: "primary.main",
          }}
        />
      )}
      <ListItemButton
        selected={isSelected}
        sx={{ pl: depth }}
        onClick={() => {
          navigate(item.path);
          if (isMobile) setDrawerOpen(false);
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText
          primary={t(`nav.${item.key}`)}
          primaryTypographyProps={{
            fontWeight: isSelected ? 700 : 500,
            fontSize: 14,
          }}
        />
        {item.key === "funnelHealth" && funnelIssueCount > 0 && (
          <Badge
            badgeContent={funnelIssueCount}
            color="warning"
            sx={{ mr: 1.5, "& .MuiBadge-badge": { position: "relative", transform: "none" } }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

function loadOpenState(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    if (typeof parsed !== "object" || !parsed) return {};
    // Phase 10.5 — Migration: alte Group-IDs auf neue Storefront-Gruppe
    // mappen, damit User-Open-State nicht verloren geht.
    const migrated: Record<string, boolean> = { ...parsed };
    if (parsed.funnel === true) {
      migrated.storefront = true;
      migrated.subFunnel = true;
    }
    if (parsed.spareparts === true) {
      migrated.storefront = true;
      migrated.subSpareparts = true;
    }
    return migrated;
  } catch {
    return {};
  }
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Sidebar ist persistent ab 1024 px (≙ kleine Desktop-Notebooks);
  // darunter wird sie zum Hamburger-Drawer. Entspricht der Redesign-
  // Leitlinie „voll responsive (Mobile/Tablet/Desktop)".
  const isMobile = useMediaQuery("(max-width: 1023.98px)");
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  // Drawer-State nachpflegen wenn der Viewport-Breakpoint wechselt,
  // sonst bleibt ein einmal geöffneter Mobile-Drawer beim Größenwechsel
  // offen / zu und blockiert den Content.
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  // Longest-Prefix-Match für die aktive Route. Ohne diesen Schritt würde
  // auf `/funnel/quiz` sowohl die Landing `/funnel` als auch `/funnel/quiz`
  // als selected markiert — weil beides `startsWith` erfüllt. Wir berechnen
  // den längsten Treffer einmal pro Render.
  const activePath = useMemo(() => {
    const paths = NAV_GROUPS.flatMap((g) => collectAllItems(g).map((i) => i.path));
    const hits = paths.filter(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
    );
    return hits.sort((a, b) => b.length - a.length)[0];
  }, [location.pathname]);

  // Aktive Top-Level-Gruppe + ggf. aktive Sub-Gruppe. Beide werden beim
  // Mount auto-geöffnet (Deep-Link findet sich sichtbar wieder).
  const { activeGroupId, activeSubGroupId } = useMemo(() => {
    for (const g of NAV_GROUPS) {
      for (const e of g.entries) {
        if (isSubGroup(e)) {
          if (e.items.some((it) => it.path === activePath)) {
            return { activeGroupId: g.id, activeSubGroupId: e.id };
          }
        } else if (e.path === activePath) {
          return { activeGroupId: g.id, activeSubGroupId: null as string | null };
        }
      }
    }
    return { activeGroupId: NAV_GROUPS[0].id, activeSubGroupId: null };
  }, [activePath]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const persisted = loadOpenState();
    // Ersten Besuch: alle Top-Level-Gruppen + Sub-Gruppen offen, danach nur User-Zustand.
    if (Object.keys(persisted).length === 0) {
      const all: Record<string, boolean> = {};
      for (const g of NAV_GROUPS) {
        all[g.id] = true;
        for (const e of g.entries) {
          if (isSubGroup(e)) all[e.id] = true;
        }
      }
      return all;
    }
    return persisted;
  });

  useEffect(() => {
    setOpenGroups((prev) => {
      const updates: Record<string, boolean> = {};
      if (!prev[activeGroupId]) updates[activeGroupId] = true;
      if (activeSubGroupId && !prev[activeSubGroupId]) updates[activeSubGroupId] = true;
      return Object.keys(updates).length === 0 ? prev : { ...prev, ...updates };
    });
  }, [activeGroupId, activeSubGroupId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {
      // storage full / disabled — ignore
    }
  }, [openGroups]);

  const toggleGroup = (id: string) =>
    setOpenGroups((s) => ({ ...s, [id]: !s[id] }));

  // Breadcrumb-Label aus der aktiven Route ableiten — ersetzt den
  // festen „Abo-Verwaltung"-Text, damit der Admin jederzeit sieht, wo
  // er gerade ist. Nutzt longest-prefix via `activePath`.
  const breadcrumbLabel = useMemo(() => {
    if (!activePath) return t("app.title");
    const item = NAV_GROUPS.flatMap((g) => collectAllItems(g)).find(
      (it) => it.path === activePath,
    );
    if (!item) return t("app.title");
    const label = t(`nav.${item.key}`);
    if (location.pathname !== item.path) {
      return `${label} · ${t("app.detail")}`;
    }
    return label;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, location.pathname, t]);

  // Datenqualitäts-Badge neben „Funnel-Datenqualität". Summiert alle
  // aktuell bekannten Problem-Counts aus dem Funnel-Health-Summary.
  // `cache-first` reicht — das Polling macht die Dashboard-Seite.
  const { data: funnelHealthData } = useQuery(GET_FUNNEL_DATA_HEALTH_SUMMARY, {
    fetchPolicy: "cache-first",
  });
  const funnelHealthSummary = funnelHealthData?.funnelDataHealth?.summary;
  const funnelIssueCount = funnelHealthSummary
    ? (funnelHealthSummary.dispensersWithoutRefillsCount ?? 0) +
      (funnelHealthSummary.refillsWithoutLineTagCount ?? 0) +
      (funnelHealthSummary.refillsWithoutCompatibleCodesCount ?? 0) +
      (funnelHealthSummary.dispensersWithMissingColorVariantCount ?? 0)
    : 0;

  const drawerContent = (
    <Box>
      <Toolbar sx={{ justifyContent: "space-between", px: 2.5 }}>
        <Box>
          <Typography
            variant="overline"
            sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.18em", lineHeight: 1 }}
          >
            Satino by Wepa
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 20,
              letterSpacing: "-0.01em",
              color: "text.primary",
              mt: 0.25,
            }}
          >
            {breadcrumbLabel}
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setDrawerOpen(false)}>
            <ChevronLeft />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ py: 1 }} component="nav">
        {NAV_GROUPS.map((group) => {
          const isOpen = openGroups[group.id] ?? true;
          const hasActiveChild = collectAllItems(group).some((it) => it.path === activePath);
          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => toggleGroup(group.id)}
                sx={{
                  py: 0.75,
                  color: hasActiveChild ? "primary.main" : "text.secondary",
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 36, color: hasActiveChild ? "primary.main" : "inherit" }}
                >
                  {group.icon}
                </ListItemIcon>
                <ListItemText
                  primary={t(group.label)}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                />
                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
              <Collapse in={isOpen} timeout={180} unmountOnExit>
                <List component="div" disablePadding>
                  {group.entries.map((entry) => {
                    if (isSubGroup(entry)) {
                      const subOpen = openGroups[entry.id] ?? true;
                      const subActive = entry.items.some((it) => it.path === activePath);
                      return (
                        <Box key={entry.id}>
                          <ListItemButton
                            onClick={() => toggleGroup(entry.id)}
                            sx={{
                              pl: 3,
                              py: 0.5,
                              color: subActive ? "primary.main" : "text.secondary",
                            }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: 32, color: subActive ? "primary.main" : "inherit" }}
                            >
                              {entry.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={t(entry.label)}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: 13,
                              }}
                            />
                            {subOpen ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </ListItemButton>
                          <Collapse in={subOpen} timeout={150} unmountOnExit>
                            <List component="div" disablePadding>
                              {entry.items.map((subItem) =>
                                renderNavItem(
                                  subItem,
                                  activePath,
                                  navigate,
                                  isMobile,
                                  setDrawerOpen,
                                  t,
                                  funnelIssueCount,
                                  /* depth */ 6,
                                ),
                              )}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    }
                    return renderNavItem(
                      entry,
                      activePath,
                      navigate,
                      isMobile,
                      setDrawerOpen,
                      t,
                      funnelIssueCount,
                      /* depth */ 4,
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2, color: "text.primary" }}
          >
            <Menu />
          </IconButton>
          <Typography
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 18,
              letterSpacing: "-0.01em",
              color: "text.primary",
            }}
          >
            Satino <Box component="span" sx={{ color: "primary.main" }}>/</Box>{" "}
            {t("app.title")}
          </Typography>
          <LanguageSwitcher />
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          mt: 8,
          ml: !isMobile && drawerOpen ? `${DRAWER_WIDTH}px` : 0,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: "background.default",
          minHeight: "100vh",
        }}
      >
        {/* Page-Content nutzt die volle Breite neben der Sidebar. Asymmetrisches
            Padding (32px links, 48px rechts) sorgt für bündigen Anschluss an
            die Sidebar links + komfortablen Atemraum zum Viewport-Rand rechts.
            Form-lastige Seiten, denen full-width zu luftig wird, kapseln ihre
            Cards selbst in eine page-lokale maxWidth — globaler Lock bleibt
            bewusst weg, damit Listen + Tabellen + Stat-Reihen den Platz nutzen. */}
        <Box
          sx={{
            maxWidth: "none",
            pl: { xs: 2, md: 4 },
            pr: { xs: 2, md: 6 },
            py: { xs: 2, md: 4 },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
